import { useEffect, useMemo, useState } from "react";

import { NovedadesExportAnalyticsHelper } from "@core/Analytics/NovedadesExportAnalyticsHelper";
import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";
import { UsersService } from "@services/UsersService";
import { ColaboratorInfo } from "@models/Colaborators";

export interface ExportNovedadesCreateResult {
    kind: "success" | "error";
    message?: string;
    errorMessage?: string;
    process?: NovedadesExportServiceModel.ProcessRecord;
}

interface UseExportNovedadesCreateScreenViewModelProps {
    isOpen: boolean;
    orgID: string;
    service: NovedadesExportService;
    usersService: UsersService;
}

const defaultSelectionMode: NovedadesExportServiceModel.EmployeeSelectionMode = "ALL_WITH_DATA";

export interface ExportNovedadesEmployeeListItem {
    id: string;
    name: string;
    legajo: string;
    phoneNumber: string;
    raw: ColaboratorInfo;
}

const getExportCreationErrorMessage = (error: unknown): string => {
    if (!(error instanceof Error)) {
        return "No se pudo crear la exportación.".localized();
    }

    const rawMessage = error.message?.trim();
    if (!rawMessage) {
        return "No se pudo crear la exportación.".localized();
    }

    try {
        const parsed = JSON.parse(rawMessage);
        return parsed?.message || parsed?.error || rawMessage;
    } catch {}

    return rawMessage;
};

const formatDateForAPI = (date: Date | null): string => {
    if (!date) {
        return "";
    }

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const dateRangeDays = (dateFrom: Date | null, dateTo: Date | null): number | undefined => {
    if (!dateFrom || !dateTo || dateFrom > dateTo) {
        return undefined;
    }
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.floor((dateTo.getTime() - dateFrom.getTime()) / millisecondsPerDay) + 1;
};

const mapEmployee = (employee: ColaboratorInfo): ExportNovedadesEmployeeListItem => {
    return {
        id: employee.id,
        name: employee.name || "Sin nombre",
        legajo: employee.legajo || "N/D",
        phoneNumber: employee.mobilePhone || "N/D",
        raw: employee,
    };
};

const buildEmployeeSearchText = (employee: ExportNovedadesEmployeeListItem): string => {
    return [
        employee.name,
        employee.legajo,
        employee.phoneNumber,
        employee.raw.dni,
        employee.raw.email,
    ].filter(Boolean).join(" ").toLowerCase().replace(/\s/g, "");
};

export const useExportNovedadesCreateScreenViewModel = ({
    isOpen,
    orgID,
    service,
    usersService,
}: UseExportNovedadesCreateScreenViewModelProps) => {
    const [title, setTitle] = useState("");
    const [dateFrom, setDateFrom] = useState<Date | null>(null);
    const [dateTo, setDateTo] = useState<Date | null>(null);
    const [employeeSelectionMode, setEmployeeSelectionMode] =
        useState<NovedadesExportServiceModel.EmployeeSelectionMode>(defaultSelectionMode);
    const [calculationProfileID, setCalculationProfileID] = useState<string | undefined>(undefined);
    const [exportProfiles, setExportProfiles] = useState<NovedadesExportServiceModel.ExportProfile[]>([]);
    const [exportProfilesLoading, setExportProfilesLoading] = useState(false);
    const [exportProfilesError, setExportProfilesError] = useState<string | null>(null);
    const [selectedEmployeeIDs, setSelectedEmployeeIDs] = useState<string[]>([]);
    const [employeeSearchField, setEmployeeSearchField] = useState("");
    const [employees, setEmployees] = useState<ExportNovedadesEmployeeListItem[]>([]);
    const [employeesLoading, setEmployeesLoading] = useState(false);
    const [employeesError, setEmployeesError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmissionResult, setLatestSubmissionResult] =
        useState<ExportNovedadesCreateResult | undefined>(undefined);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        setTitle("");
        setDateFrom(null);
        setDateTo(null);
        setEmployeeSelectionMode(defaultSelectionMode);
        setCalculationProfileID(undefined);
        setSelectedEmployeeIDs([]);
        setEmployeeSearchField("");
        setLatestSubmissionResult(undefined);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || !orgID) {
            return;
        }

        let cancelled = false;

        const loadProfiles = async () => {
            setExportProfilesLoading(true);
            setExportProfilesError(null);

            try {
                const result = await service.listProfiles(orgID);
                if (cancelled) {
                    return;
                }

                setExportProfiles(result);
                setCalculationProfileID((currentProfileID) => {
                    if (currentProfileID && result.some((profile) => profile.profileID === currentProfileID)) {
                        return currentProfileID;
                    }
                    return result[0]?.profileID;
                });
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    setExportProfiles([]);
                    setCalculationProfileID(undefined);
                    setExportProfilesError("No se pudieron cargar los perfiles de exportación.".localized());
                }
            } finally {
                if (!cancelled) {
                    setExportProfilesLoading(false);
                }
            }
        };

        void loadProfiles();

        return () => {
            cancelled = true;
        };
    }, [isOpen, orgID, service]);

    useEffect(() => {
        if (!isOpen || !orgID) {
            return;
        }

        let cancelled = false;

        const loadEmployees = async () => {
            setEmployeesLoading(true);
            setEmployeesError(null);

            try {
                const result = await usersService.getNomina(orgID);
                if (cancelled) {
                    return;
                }

                setEmployees(
                    (result ?? [])
                        .filter((employee: ColaboratorInfo) => !employee.deactivationTimestamp && employee.id)
                        .map(mapEmployee)
                        .sort((left, right) => left.name.localeCompare(right.name))
                );
            } catch (error) {
                console.error(error);
                if (!cancelled) {
                    setEmployees([]);
                    setEmployeesError("No se pudieron cargar los colaboradores.".localized());
                }
            } finally {
                if (!cancelled) {
                    setEmployeesLoading(false);
                }
            }
        };

        void loadEmployees();

        return () => {
            cancelled = true;
        };
    }, [isOpen, orgID, usersService]);

    const selectedEmployeeIDsRequired = employeeSelectionMode !== "ALL_WITH_DATA";
    const filteredEmployees = useMemo(() => {
        const normalizedSearch = employeeSearchField.toLowerCase().replace(/\s/g, "");

        if (!normalizedSearch) {
            return employees;
        }

        return employees.filter((employee) => buildEmployeeSearchText(employee).includes(normalizedSearch));
    }, [employeeSearchField, employees]);
    const dateFromValue = useMemo(() => formatDateForAPI(dateFrom), [dateFrom]);
    const dateToValue = useMemo(() => formatDateForAPI(dateTo), [dateTo]);
    const hasValidDateRange = Boolean(dateFrom && dateTo && dateFrom <= dateTo);
    const canSubmit = Boolean(
        orgID &&
        title.trim() &&
        hasValidDateRange &&
        (!selectedEmployeeIDsRequired || selectedEmployeeIDs.length > 0) &&
        !exportProfilesLoading &&
        !employeesLoading &&
        !isSubmitting
    );

    const setAllEmployeesMode = () => {
        setEmployeeSelectionMode("ALL_WITH_DATA");
        setSelectedEmployeeIDs([]);
    };

    const setSpecificEmployeesMode = () => {
        setEmployeeSelectionMode("SELECTED");
    };

    const toggleEmployee = (employeeID: string) => {
        setSelectedEmployeeIDs((currentIDs) => {
            if (currentIDs.includes(employeeID)) {
                return currentIDs.filter((id) => id !== employeeID);
            }
            return [...currentIDs, employeeID];
        });
    };

    const isEmployeeSelected = (employeeID: string) => selectedEmployeeIDs.includes(employeeID);

    const submit = async (): Promise<ExportNovedadesCreateResult | undefined> => {
        if (!canSubmit) {
            return undefined;
        }
        const analyticsPayload = {
            selectionMode: employeeSelectionMode,
            selectedEmployeeCount: selectedEmployeeIDsRequired ? selectedEmployeeIDs.length : 0,
            hasDateRange: hasValidDateRange,
            dateRangeDays: dateRangeDays(dateFrom, dateTo),
            calculationProfileID,
            titleLength: title.trim().length,
        };

        setIsSubmitting(true);
        setLatestSubmissionResult(undefined);
        NovedadesExportAnalyticsHelper.trackCreateSubmitted(analyticsPayload);

        try {
            const process = await service.startProcess({
                organizationID: orgID,
                title: title.trim(),
                dateFrom: dateFromValue,
                dateTo: dateToValue,
                employeeSelectionMode,
                selectedEmployeeIDs: selectedEmployeeIDsRequired ? selectedEmployeeIDs : undefined,
                calculationProfileID,
            });

            const result = {
                kind: "success" as const,
                message: "Exportación creada correctamente.".localized(),
                process,
            };
            NovedadesExportAnalyticsHelper.trackCreateSucceeded({
                ...analyticsPayload,
                processID: process.processID,
            });
            setLatestSubmissionResult(result);
            return result;
        } catch (error) {
            const errorMessage = getExportCreationErrorMessage(error);
            const result = {
                kind: "error" as const,
                errorMessage,
            };
            NovedadesExportAnalyticsHelper.trackCreateFailed({
                ...analyticsPayload,
                errorMessage,
            });
            setLatestSubmissionResult(result);
            return result;
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        title,
        setTitle,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        dateFromValue,
        dateToValue,
        calculationProfileID,
        setCalculationProfileID,
        exportProfiles,
        exportProfilesLoading,
        exportProfilesError,
        employeeSelectionMode,
        setAllEmployeesMode,
        setSpecificEmployeesMode,
        employees,
        filteredEmployees,
        employeeSearchField,
        setEmployeeSearchField,
        employeesLoading,
        employeesError,
        selectedEmployeeIDs,
        toggleEmployee,
        isEmployeeSelected,
        selectedEmployeeIDsRequired,
        hasValidDateRange,
        isSubmitting,
        latestSubmissionResult,
        canSubmit,
        submit,
    };
};
